import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  GraduationCap,
  Plus,
  RefreshCw,
  Copy,
  Check,
  Ban,
  Download,
  Clock,
  KeyRound,
  Mail,
  X,
  Eye,
  EyeOff,
  Loader2,
  Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  active: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  expired: 'bg-amber-50 border-amber-200 text-amber-700',
  revoked: 'bg-rose-50 border-rose-200 text-rose-700',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const authHeaders = () => {
  const t = localStorage.getItem('mosaic_session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// -------------------------------------------------------------------------
// One-time password reveal modal — shown immediately after create or reset
// -------------------------------------------------------------------------
function OneTimePasswordModal({ record, onClose }) {
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(true);
  if (!record) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(record.one_time_password);
      setCopied(true);
      toast.success('Password copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      window.prompt('Copy this one-time password:', record.one_time_password);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="otp-modal">
      <div className="bg-white w-full max-w-lg border border-amber-300">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between bg-amber-50">
          <div className="flex items-center gap-2">
            <KeyRound size={16} className="text-amber-700" />
            <h3 className="text-sm font-bold text-amber-900">One-time password — shown only once</h3>
          </div>
          <button onClick={onClose} data-testid="otp-close"><X size={18} /></button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <div className="text-xs text-gray-600">
            Copy this password now. It will <strong>not</strong> be shown again.
            {record.welcome_email_sent ? (
              <span className="block mt-2 text-emerald-700">
                ✓ A welcome email with these credentials and a PDF copy has been sent to{' '}
                <strong>{record.portal_user?.email}</strong>.
              </span>
            ) : (
              <span className="block mt-2 text-amber-700">
                ⚠ Welcome PDF was saved, but the email could not be sent automatically.
                You can re-send from the table actions.
              </span>
            )}
          </div>
          <div className="bg-gray-50 border border-gray-200 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Username</div>
            <div className="text-sm font-mono text-gray-900">{record.portal_user?.email}</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 flex items-center justify-between">
              <span>One-time password</span>
              <button onClick={() => setVisible(!visible)} className="hover:text-gray-700" data-testid="otp-toggle-visible">
                {visible ? <EyeOff size={12} /> : <Eye size={12} />}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <code className="text-base font-mono font-bold text-gray-900 flex-1" data-testid="otp-value">
                {visible ? record.one_time_password : '•'.repeat(record.one_time_password.length)}
              </code>
              <button
                onClick={copy}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-[#002FA7] text-white hover:bg-blue-800 transition-colors"
                data-testid="otp-copy"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            Teacher Portal URL: <code className="text-[#002FA7]">/teacher-portal/login</code> &middot;
            On first login the teacher will be forced to change this password.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-xs font-medium bg-gray-800 text-white hover:bg-gray-900"
            data-testid="otp-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Create portal user modal
// -------------------------------------------------------------------------
function CreatePortalUserModal({ orders, loadingOrders, onCreated, onClose }) {
  const [email, setEmail] = useState('');
  const [teacherName, setTeacherName] = useState('');
  const [orderId, setOrderId] = useState('');
  // Default expiry: 90 days from today
  const defaultExpiry = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 90);
    return d.toISOString().slice(0, 10);
  })();
  const [expiresDate, setExpiresDate] = useState(defaultExpiry);
  const [submitting, setSubmitting] = useState(false);
  // v6.32.90 — manual-delivery / no-email path.
  const [skipEmail, setSkipEmail] = useState(false);
  const [skipReason, setSkipReason] = useState('');

  const submit = async () => {
    if (!email.trim() || !teacherName.trim() || !orderId || !expiresDate) {
      toast.error('Please fill in every field');
      return;
    }
    if (skipEmail && skipReason.trim().length < 3) {
      toast.error('Manual delivery reason is required (≥ 3 characters)');
      return;
    }
    // Send as end-of-day UTC for clarity
    const expiresAt = new Date(`${expiresDate}T23:59:59Z`).toISOString();
    setSubmitting(true);
    try {
      const payload = {
        email: email.trim().toLowerCase(),
        teacher_name: teacherName.trim(),
        order_id: orderId,
        expires_at: expiresAt,
      };
      if (skipEmail) {
        payload.skip_welcome_email = true;
        payload.skip_welcome_email_reason = skipReason.trim();
      }
      const { data } = await axios.post(
        `${API}/api/superadmin/portal-users`,
        payload,
        { headers: authHeaders() }
      );
      toast.success(
        skipEmail
          ? 'Portal account created — manual delivery required'
          : 'Teacher Portal account created'
      );
      onCreated(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // v6.32.90 — surface the structured 409 (active/expired duplicate) helpful message.
      if (detail && typeof detail === 'object' && detail.guidance) {
        toast.error(detail.reason || 'Could not create account', {
          description: detail.guidance,
        });
      } else {
        toast.error('Could not create account', {
          description: typeof detail === 'string' ? detail : 'Please check the fields and try again.',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-portal-modal">
      <div className="bg-white w-full max-w-md border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">Create Teacher Portal Access</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <div>
            <label className="text-xs font-semibold block mb-1">Teacher email (= username) *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ms.garcia@schoolemail.edu"
              className="w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-[#002FA7]"
              data-testid="create-portal-email"
            />
          </div>
          <div>
            <label className="text-xs font-semibold block mb-1">Teacher name *</label>
            <input
              type="text"
              value={teacherName}
              onChange={(e) => setTeacherName(e.target.value)}
              placeholder="Ms. Garcia"
              className="w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-[#002FA7]"
              data-testid="create-portal-name"
            />
          </div>
          <div>
            <label className="text-xs font-semibold block mb-1">Linked order *</label>
            <select
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-[#002FA7]"
              data-testid="create-portal-order"
              disabled={loadingOrders}
            >
              <option value="">{loadingOrders ? 'Loading orders…' : 'Select an order…'}</option>
              {orders.map((o) => (
                <option key={o.order_id} value={o.order_id}>
                  {o.order_number} — {o.school_name || o.customer_name || 'no school'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold block mb-1">Access expires on *</label>
            <input
              type="date"
              value={expiresDate}
              onChange={(e) => setExpiresDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-[#002FA7]"
              data-testid="create-portal-expires"
            />
            <p className="text-[10px] text-gray-400 mt-1">Teacher access is revoked automatically at 23:59 UTC on this date.</p>
          </div>

          {/* v6.32.90 — manual-delivery / no-email checkbox */}
          <div className="border-t border-gray-200 pt-4">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={skipEmail}
                onChange={(e) => setSkipEmail(e.target.checked)}
                className="mt-0.5"
                data-testid="create-portal-skip-email"
              />
              <span className="text-xs">
                <span className="font-semibold text-amber-900">Manual delivery — do NOT send a welcome email</span>
                <span className="block text-[10px] text-gray-500 mt-0.5">
                  MOSAIC will generate the credentials + welcome PDF, but will NOT send any SendGrid email. You will deliver the PDF / password to the teacher yourself.
                </span>
              </span>
            </label>
            {skipEmail && (
              <div className="mt-3 ml-6 space-y-2" data-testid="create-portal-skip-banner">
                <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 leading-snug">
                  <strong>⚠ Manual delivery mode</strong>
                  <ol className="list-decimal ml-5 mt-1 space-y-0.5">
                    <li>The one-time password will be shown on screen ONCE after creation.</li>
                    <li>The welcome PDF will be saved to R2 and downloadable from the row's PDF button.</li>
                    <li>Download both, deliver to the teacher manually (your own email or in person).</li>
                    <li>Do NOT click "Re-send" afterwards — that would trigger an email.</li>
                  </ol>
                </div>
                <div>
                  <label className="text-xs font-semibold block mb-1 text-amber-900">
                    Reason for skipping email (required, ≥ 3 chars) *
                  </label>
                  <input
                    type="text"
                    value={skipReason}
                    onChange={(e) => setSkipReason(e.target.value)}
                    maxLength={500}
                    placeholder="e.g. already emailed credentials manually 2026-05-05"
                    className="w-full border border-amber-300 px-3 py-1.5 text-sm focus:outline-none focus:border-amber-600 bg-amber-50"
                    data-testid="create-portal-skip-reason"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60 ${skipEmail ? 'bg-amber-700 hover:bg-amber-800' : 'bg-[#002FA7] hover:bg-blue-800'}`}
            data-testid="create-portal-submit"
          >
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            {submitting ? 'Creating…' : (skipEmail ? 'Create (no email)' : 'Create access')}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Extend expiration modal
// -------------------------------------------------------------------------
function ExtendModal({ user, onDone, onClose }) {
  const current = user?.expires_at?.slice(0, 10) || new Date().toISOString().slice(0, 10);
  const [expiresDate, setExpiresDate] = useState(current);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const expiresAt = new Date(`${expiresDate}T23:59:59Z`).toISOString();
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/superadmin/portal-users/${user.portal_user_id}/extend`,
        { expires_at: expiresAt },
        { headers: authHeaders() }
      );
      toast.success('Access extended');
      onDone();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error('Could not extend', { description: typeof detail === 'string' ? detail : undefined });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="extend-portal-modal">
      <div className="bg-white w-full max-w-sm border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">Extend Teacher Portal Access</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="px-5 py-5 space-y-3">
          <div className="text-xs text-gray-500">
            <div><strong>{user.email}</strong></div>
            <div>Order {user.order_number}</div>
          </div>
          <div>
            <label className="text-xs font-semibold block mb-1">New expiration date</label>
            <input
              type="date"
              value={expiresDate}
              onChange={(e) => setExpiresDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-[#002FA7]"
              data-testid="extend-portal-date"
            />
            <p className="text-[10px] text-gray-400 mt-1">Setting a future date also un-revokes a revoked account.</p>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#002FA7] text-white hover:bg-blue-800 disabled:opacity-60"
            data-testid="extend-portal-submit"
          >
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <Clock size={12} />}
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Main tab
// -------------------------------------------------------------------------
export default function TeacherPortalUsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [otpRecord, setOtpRecord] = useState(null);
  const [extendUser, setExtendUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [actingOn, setActingOn] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);  // v6.32.89 — hard-delete modal state

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const { data } = await axios.get(`${API}/api/superadmin/portal-users${qs}`, { headers: authHeaders() });
      setUsers(data.portal_users || []);
    } catch (err) {
      console.error(err);
      toast.error('Could not load portal accounts');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const { data } = await axios.get(`${API}/api/orders`, { headers: authHeaders() });
      setOrders(data.orders || data || []);
    } catch {
      // non-blocking
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleRevoke = async (user) => {
    if (!window.confirm(`Revoke portal access for ${user.email}?\nThis terminates their session immediately.`)) return;
    setActingOn(user.portal_user_id);
    try {
      await axios.post(`${API}/api/superadmin/portal-users/${user.portal_user_id}/revoke`, null, { headers: authHeaders() });
      toast.success('Access revoked');
      fetchList();
    } catch (err) {
      toast.error('Could not revoke');
    } finally {
      setActingOn(null);
    }
  };

  const handleResetPassword = async (user) => {
    if (!window.confirm(`Generate a new one-time password for ${user.email}?\nThe current password will stop working.`)) return;
    setActingOn(user.portal_user_id);
    try {
      const { data } = await axios.post(`${API}/api/superadmin/portal-users/${user.portal_user_id}/reset-password`, null, { headers: authHeaders() });
      toast.success('Password reset — new one-time password generated');
      setOtpRecord(data);
      fetchList();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error('Could not reset password', { description: typeof detail === 'string' ? detail : undefined });
    } finally {
      setActingOn(null);
    }
  };

  const handleResend = async (user) => {
    if (!window.confirm(`Re-send welcome email + regenerate saved PDF for ${user.email}?\nThis does NOT change the password — use Reset Password if the teacher needs a new working password.`)) return;
    setActingOn(user.portal_user_id);
    try {
      const { data } = await axios.post(`${API}/api/superadmin/portal-users/${user.portal_user_id}/resend-welcome`, null, { headers: authHeaders() });
      if (data.welcome_email_sent) {
        toast.success('Welcome email re-sent and PDF re-saved');
      } else {
        toast.warning('Welcome PDF re-saved, but email could not be sent', {
          description: 'Check SendGrid configuration. The PDF is downloadable below.',
        });
      }
      fetchList();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error('Could not re-send', { description: typeof detail === 'string' ? detail : undefined });
    } finally {
      setActingOn(null);
    }
  };

  const handleDownloadWelcomePdf = async (user) => {
    setActingOn(user.portal_user_id);
    try {
      const resp = await axios.get(
        `${API}/api/superadmin/portal-users/${user.portal_user_id}/welcome-pdf`,
        { headers: authHeaders(), responseType: 'blob' }
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const filename = `MOSAIC_Teacher_Portal_Welcome_${user.order_number || 'unknown'}_${(user.email || '').replace('@', '_at_')}.pdf`;
      // Safari kills the blob URL too fast on synchronous revoke + ignores the
      // `download` attribute on programmatic <a> clicks. Detect Safari and
      // fall back to window.open(); revoke after a delay so Chrome/FF still
      // get the download attribute behavior.
      const ua = navigator.userAgent;
      const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
      if (isSafari) {
        const win = window.open(url, '_blank');
        if (!win) {
          // Pop-up blocked: render an in-place link the user can right-click → Save As.
          toast.warning('Pop-up blocked', {
            description: 'Allow pop-ups for mosaicassessmentco.com, or hold ⌥ + click the PDF button to save.',
          });
        }
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Delay revoke so Safari's <iframe>/window.open and Firefox's slower
      // download path both have time to fetch the blob before it dies.
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      toast.success('Welcome PDF downloaded');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Could not download saved PDF';
      toast.error(msg);
    } finally {
      setActingOn(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="teacher-portal-users-tab">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <GraduationCap size={20} /> Teacher Portal Accounts
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Controlled print-only teacher access. Accounts are tied to a specific order and
            auto-expire on the date you choose. Teachers log in at
            <code className="mx-1 text-[#002FA7]">/teacher-portal/login</code>
            and are blocked from every other part of MOSAIC.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 px-2 py-1.5 text-xs"
            data-testid="portal-filter-status"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
          </select>
          <button
            onClick={fetchList}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            data-testid="portal-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#002FA7] text-white hover:bg-blue-800"
            data-testid="portal-create-new"
          >
            <Plus size={12} /> New portal account
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400" data-testid="portal-loading">Loading portal accounts…</div>
      ) : users.length === 0 ? (
        <div className="bg-white border border-gray-200 px-5 py-12 text-center text-gray-500 text-sm" data-testid="portal-empty">
          No Teacher Portal accounts yet.{' '}
          <button
            onClick={() => setShowCreate(true)}
            className="text-[#002FA7] hover:underline"
            data-testid="portal-empty-create"
          >
            Create the first one
          </button>
          .
        </div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-hidden" data-testid="portal-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-xs uppercase tracking-wider text-gray-500">
                <th className="text-left px-4 py-2.5 font-semibold">Teacher</th>
                <th className="text-left px-4 py-2.5 font-semibold">Order</th>
                <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                <th className="text-left px-4 py-2.5 font-semibold">Expires</th>
                <th className="text-left px-4 py-2.5 font-semibold">Activated</th>
                <th className="text-left px-4 py-2.5 font-semibold">Welcome email</th>
                <th className="text-left px-4 py-2.5 font-semibold">Last login</th>
                <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u) => {
                const busy = actingOn === u.portal_user_id;
                return (
                  <tr key={u.portal_user_id} className="hover:bg-gray-50" data-testid={`portal-row-${u.portal_user_id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-gray-900">{u.teacher_name || '—'}</div>
                      <div className="text-xs text-gray-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-mono text-xs text-gray-700">{u.order_number || '—'}</div>
                      <div className="text-xs text-gray-500">{u.school_name || ''}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold uppercase border ${STATUS_BADGE[u.status] || STATUS_BADGE.active}`}>
                        {u.status}
                      </span>
                      {u.welcome_email_skipped && (
                        <span
                          className="ml-1.5 inline-flex px-1.5 py-0.5 text-[9px] font-semibold uppercase border bg-amber-50 border-amber-300 text-amber-800"
                          title={`Manual delivery — no email sent. Reason: ${u.welcome_email_skipped_reason || '—'}`}
                          data-testid={`portal-row-manual-delivery-${u.portal_user_id}`}
                        >
                          Manual
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600">{fmtDate(u.expires_at)}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-600">{fmtDate(u.activated_at)}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-600">{fmtDate(u.welcome_email_sent_at)}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-600">{fmtDate(u.last_login_at)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setExtendUser(u)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 disabled:opacity-50"
                          data-testid={`portal-extend-${u.portal_user_id}`}
                          title="Change expiration date (also un-revokes)"
                        >
                          <Clock size={11} /> Extend
                        </button>
                        <button
                          onClick={() => handleResetPassword(u)}
                          disabled={busy || u.status === 'revoked'}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-[#002FA7] bg-white hover:bg-blue-50 border border-blue-200 disabled:opacity-50"
                          data-testid={`portal-reset-${u.portal_user_id}`}
                          title="Generate a new one-time password"
                        >
                          <KeyRound size={11} /> Reset pwd
                        </button>
                        <button
                          onClick={() => handleDownloadWelcomePdf(u)}
                          disabled={busy || !u.welcome_pdf_r2_key}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-emerald-700 bg-white hover:bg-emerald-50 border border-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid={`portal-download-welcome-${u.portal_user_id}`}
                          title={u.welcome_pdf_r2_key ? 'Download / print / save the welcome letter PDF' : 'No saved PDF yet — re-send to generate'}
                        >
                          <Download size={11} /> PDF
                        </button>
                        <button
                          onClick={() => handleResend(u)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 disabled:opacity-50"
                          data-testid={`portal-resend-${u.portal_user_id}`}
                          title="Re-send the welcome email + PDF — does NOT generate a new password. To issue a new working password, click Reset pwd instead."
                        >
                          <Mail size={11} /> Re-send (no new pw)
                        </button>
                        {u.status !== 'revoked' && (
                          <button
                            onClick={() => handleRevoke(u)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-rose-700 bg-white hover:bg-rose-50 border border-rose-200 disabled:opacity-50"
                            data-testid={`portal-revoke-${u.portal_user_id}`}
                            title="Revoke immediately"
                          >
                            <Ban size={11} /> Revoke
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteUser(u)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-rose-900 bg-white hover:bg-rose-100 border border-rose-300 disabled:opacity-50"
                          data-testid={`portal-delete-${u.portal_user_id}`}
                          title="Permanently delete (typo / wrong recipient)"
                        >
                          <Trash2 size={11} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreatePortalUserModal
          orders={orders}
          loadingOrders={loadingOrders}
          onCreated={(data) => {
            setShowCreate(false);
            setOtpRecord(data);
            fetchList();
          }}
          onClose={() => setShowCreate(false)}
        />
      )}
      {otpRecord && <OneTimePasswordModal record={otpRecord} onClose={() => setOtpRecord(null)} />}
      {extendUser && (
        <ExtendModal
          user={extendUser}
          onDone={() => {
            setExtendUser(null);
            fetchList();
          }}
          onClose={() => setExtendUser(null)}
        />
      )}
      {deleteUser && (
        <DeletePortalUserModal
          user={deleteUser}
          onDone={() => { setDeleteUser(null); fetchList(); }}
          onClose={() => setDeleteUser(null)}
        />
      )}
    </div>
  );
}

// =========================================================================
// v6.32.89 — Hard-delete confirmation modal.
// Requires the operator to type the EMAIL exactly + supply a reason.
// =========================================================================
function DeletePortalUserModal({ user, onDone, onClose }) {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [reason, setReason] = useState('');
  const [deletePdf, setDeletePdf] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const emailMatches = confirmEmail.trim().toLowerCase() === (user.email || '').toLowerCase();
  const canSubmit = emailMatches && reason.trim().length >= 3 && !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await axios.delete(
        `${API}/api/superadmin/portal-users/${user.portal_user_id}`,
        {
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          data: {
            confirm_email: confirmEmail.trim(),
            reason: reason.trim(),
            delete_welcome_pdf: deletePdf,
          },
        }
      );
      const bits = [`sessions: ${data.sessions_deleted}`];
      if (deletePdf) bits.push(`pdf: ${data.welcome_pdf_deleted ? 'deleted' : 'failed'}`);
      toast.success(`Portal user deleted (${bits.join(', ')})`);
      onDone();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error('Could not delete portal user', { description: typeof detail === 'string' ? detail : undefined });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="portal-delete-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-lg border border-rose-300 shadow-lg">
        <div className="px-5 py-3 border-b border-rose-200 bg-rose-50 flex items-center gap-2">
          <Trash2 size={16} className="text-rose-700" />
          <h2 className="text-sm font-bold text-rose-900">Permanently delete portal user</h2>
        </div>
        <div className="px-5 py-4 space-y-4 text-sm">
          <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
            <strong>This is irreversible.</strong> The portal_users record will be removed.
            For normal end-of-life cleanup, prefer <strong>Revoke</strong> (preserves audit trail on the doc itself).
            An audit log entry with the full pre-delete snapshot will be kept in <code>portal_user_audit_log</code> regardless.
          </div>
          <dl className="grid grid-cols-[100px_1fr] gap-y-1 text-xs text-gray-700">
            <dt className="font-semibold">Email</dt><dd className="font-mono">{user.email}</dd>
            <dt className="font-semibold">Order</dt><dd className="font-mono">{user.order_number || '—'}</dd>
            <dt className="font-semibold">Status</dt><dd>{user.status}</dd>
            <dt className="font-semibold">PO #</dt><dd className="font-mono">{user.po_number || '—'}</dd>
          </dl>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Type <span className="font-mono bg-gray-100 px-1">{user.email}</span> to confirm
            </label>
            <input
              type="text"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              autoFocus
              autoComplete="off"
              className={`w-full px-3 py-2 text-sm font-mono border ${emailMatches ? 'border-emerald-300 bg-emerald-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-rose-300`}
              data-testid="portal-delete-confirm-email"
              placeholder={user.email}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Reason (required, ≥3 chars)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              className="w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-rose-300"
              data-testid="portal-delete-reason"
              placeholder="e.g. typo on email — recreated correct account"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={deletePdf}
              onChange={(e) => setDeletePdf(e.target.checked)}
              data-testid="portal-delete-pdf-checkbox"
            />
            Also remove the saved welcome PDF from R2 storage
          </label>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-xs font-medium border border-gray-300 hover:bg-white"
            data-testid="portal-delete-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-semibold bg-rose-700 hover:bg-rose-800 disabled:bg-rose-300 text-white"
            data-testid="portal-delete-submit"
          >
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            {submitting ? 'Deleting…' : 'Permanently delete'}
          </button>
        </div>
      </form>
    </div>
  );
}
